import CityCenterIcon from '@skyscanner/backpack-web/bpk-component-icon/sm/city-center';
import ContentEventIcon from '@skyscanner/backpack-web/bpk-component-icon/sm/content--event';
import PoiIcon from '@skyscanner/backpack-web/bpk-component-icon/sm/landmark';
import LocationIcon from '@skyscanner/backpack-web/bpk-component-icon/sm/location';
import TrainIcon from '@skyscanner/backpack-web/bpk-component-icon/sm/train';

import DescriptionItem from './DescriptionItem';

import type { MiniEventTracker } from '@shared/types/frontend/mini-event-tracker';
import type { PoiDataArray } from '@shared/types/frontend/nearby-transport-response';
import type { Entity } from '@shared/types/hotel-content';
import type { EntityId, Nullable } from '@shared/types/utils';

import iconMap from '@hotels-hotel-detail/ui/PoiIconMap';
import {
  MAPPING_MAP_CLICK_TYPE,
  THING_TO_DO,
  NEAREST_POINT,
  TRANSPORT,
  NEAREST_TRANSPORTATION,
  CITY_CENTER,
  ADDRESS,
} from '@hotels-hotel-detail/utils/location-constants';
import { buildAdditionalInfoNearbyMap } from '@hotels-hotel-detail/utils/mini-events/hotels-action';
import { HASH_FULL_MAP } from '@shared/constants/hash-routes';
import { withRouterLink } from '@shared/ui/common/RouteLink';
import { FormattedDistance } from '@shared/ui/components/i18n';
import { withMiniEventTracker } from '@shared/ui/contexts/mini-events';
import {
  ACTION_TYPE,
  CLICK_TYPE,
} from '@shared/utils-frontend/mini-events/constants';

const DescriptionItemRouter = withRouterLink(DescriptionItem, HASH_FULL_MAP);

type Props = {
  nearbyPoi: PoiDataArray;
  airport: PoiDataArray;
  transportWithoutAirport: PoiDataArray;
  distanceText?: string;
  address: string;
  onTabClick: (name: string) => void;
  onPoiMarkerClick: (selectedEntityId: Nullable<EntityId>) => void;
  entity?: Nullable<Entity>;
  hotelId: string;
  miniEventTracker: MiniEventTracker;
};
const DescriptionPanel = ({
  address,
  airport,
  distanceText,
  entity,
  hotelId,
  miniEventTracker,
  nearbyPoi,
  onPoiMarkerClick,
  onTabClick,
  transportWithoutAirport,
}: Props) => {
  const trackClick = (
    mapClickType: string,
    selectedPoi: Nullable<{
      entityId: EntityId;
      linearDistance: number;
    }>,
  ) => {
    miniEventTracker.trackHotelsAction(
      ACTION_TYPE.DETAIL_PAGE_NEARBY_MAP_CLICK,
      buildAdditionalInfoNearbyMap({
        isMapFullView: false,
        hotelId: parseInt(hotelId, 10),
        clickType: CLICK_TYPE[MAPPING_MAP_CLICK_TYPE[mapClickType]],
        poiInfo: selectedPoi
          ? {
              poi_id: parseInt(selectedPoi.entityId, 10),
              distance_in_meters: selectedPoi.linearDistance,
            }
          : null,
      }),
    );
  };

  const descriptions = [];
  if (distanceText && entity) {
    let distanceIcon;
    let type;
    const isCity = entity.type === 'City';
    if (isCity) {
      distanceIcon = CityCenterIcon;
      type = entity.type;
    } else if (iconMap[entity.type]) {
      distanceIcon = iconMap[entity.type].sm;
      type = entity.type;
    } else {
      distanceIcon = PoiIcon;
    }
    descriptions.push({
      content: distanceText,
      Icon: distanceIcon,
      key: 'distance',
      type,
      onClick: () => {
        onTabClick(THING_TO_DO);
        if (nearbyPoi.length > 0) {
          onPoiMarkerClick(nearbyPoi[0].entityId);
        }

        trackClick(CITY_CENTER, null);
      },
    });
  }

  const transports = transportWithoutAirport
    .concat(airport)
    .sort((a, b) => a.linearDistance - b.linearDistance);
  if (transports.length > 0) {
    const firstTransport = transports[0];
    descriptions.push({
      Icon: TrainIcon,
      key: 'transport',
      onClick: () => {
        onTabClick(TRANSPORT);
        onPoiMarkerClick(firstTransport.entityId);
        trackClick(NEAREST_TRANSPORTATION, {
          entityId: firstTransport.entityId,
          linearDistance: firstTransport.linearDistance,
        });
      },
      content: (
        <FormattedDistance
          distanceMeters={firstTransport.linearDistance}
          options={{
            kmString: 'Distance_label_fromDestinationKm',
            milesString: 'Distance_label_fromDestinationMiles',
          }}
          data={{
            destination: firstTransport.poiName,
          }}
        />
      ),
    });
  } else if (nearbyPoi.length > 0) {
    descriptions.push({
      Icon: ContentEventIcon,
      key: 'acctrations',
      onClick: () => {
        onTabClick(THING_TO_DO);
        trackClick(NEAREST_POINT, null);
      },
      content: (
        <FormattedDistance
          distanceMeters={nearbyPoi[nearbyPoi.length - 1].linearDistance}
          options={{
            kmString: 'Distance_label_acctractionsWithinKmOther',
            milesString: 'Distance_label_acctractionsWithinMilesOther',
          }}
          data={{
            number: nearbyPoi.length,
          }}
        />
      ),
    });
  }
  descriptions.push({
    key: 'address',
    Icon: LocationIcon,
    content: address,
    onClick: () => {
      onTabClick(THING_TO_DO);
      trackClick(ADDRESS, null);
    },
  });

  return (
    <div>
      {descriptions.map(({ Icon, content, key, onClick, type }) => (
        <DescriptionItemRouter
          key={key}
          Icon={Icon}
          content={content}
          onClick={onClick}
          hotelId={hotelId}
          descItemKey={key}
          type={type}
        />
      ))}
    </div>
  );
};

export default withMiniEventTracker(DescriptionPanel);
